import React, { useState, useEffect, useCallback } from "react";
import { Save, Loader2, RotateCcw, Info } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FeatureCategoryBadge, FeatureSourceBadge } from "./FeatureBadges";

/**
 * TenantFeatureOverrideEditor — reusable editor for Tenant Feature Override.
 * Props:
 *   tenantId (required)
 *   embedded (boolean) — kalau true, tanpa header (untuk embed di TenantDetail)
 *   onChanged() — callback setelah simpan berhasil
 */
export default function TenantFeatureOverrideEditor({ tenantId, embedded = false, onChanged }) {
  const [data, setData] = useState(null); // { features: [...], enabled_keys: [], package }
  const [drafts, setDrafts] = useState({}); // { feature_key: { enabled, reason, changed } }
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const r = await api.get(`/platform/tenants/${tenantId}/effective-features`);
      setData(r.data);
      // Seed drafts dari override yang existing
      const d = {};
      (r.data.features || []).forEach((f) => {
        if (f.source === "override") {
          d[f.feature_key] = { enabled: f.enabled, reason: f.override_reason || "", changed: false };
        }
      });
      setDrafts(d);
    } catch (e) { notifyError(e, "Gagal memuat effective features"); } finally { setLoading(false); }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  const setOverride = (key, enabled, reason) => {
    setDrafts((prev) => ({
      ...prev,
      [key]: { enabled, reason: reason ?? (prev[key]?.reason || ""), changed: true },
    }));
  };
  const clearOverride = (key) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const overrides = Object.entries(drafts).map(([feature_key, v]) => ({
        feature_key, enabled: !!v.enabled, override_reason: v.reason || "",
      }));
      await api.put(`/platform/tenants/${tenantId}/features`, { overrides });
      toast.success(`Override tersimpan (${overrides.length} entry).`);
      onChanged?.();
      load();
    } catch (e) { notifyError(e, "Gagal menyimpan override"); } finally { setSaving(false); }
  };

  if (loading || !data) {
    return <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center" data-testid="override-loading">Memuat…</div>;
  }

  const grouped = (data.features || []).reduce((acc, f) => {
    const c = f.feature_category || "other";
    (acc[c] = acc[c] || []).push(f);
    return acc;
  }, {});

  const totalOverride = Object.keys(drafts).length;

  return (
    <div data-testid="tenant-override-editor">
      {!embedded && (
        <div className="text-xs text-white/60 mb-3 flex items-center gap-2">
          <Info size={12} /> Package: <span className="font-black text-white">{data.package}</span> · Enabled: <span className="text-emerald-400 font-bold">{data.enabled_keys.length}</span> · Override: <span className="text-amber-400 font-bold">{totalOverride}</span>
        </div>
      )}

      <div className="flex items-center gap-3 mb-3">
        <span className="text-xs text-white/50">
          Package: <span className="text-white font-bold">{data.package}</span> · Total override: <span className="font-bold text-amber-400" data-testid="override-count">{totalOverride}</span>
        </span>
        <Button onClick={save} disabled={saving} className="ml-auto bg-emerald-500 text-white hover:bg-emerald-600" data-testid="btn-save-overrides">
          {saving ? <><Loader2 size={14} className="mr-1 animate-spin" /> Menyimpan…</> : <><Save size={14} className="mr-1" /> Simpan Override</>}
        </Button>
      </div>

      <div className="space-y-3">
        {Object.entries(grouped).map(([cat, feats]) => (
          <div key={cat} className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="px-4 py-2 bg-white/5"><FeatureCategoryBadge category={cat} /></div>
            {feats.map((f) => {
              const hasDraft = f.feature_key in drafts;
              const draft = drafts[f.feature_key];
              // Kalau ada draft, itu source override. Kalau tidak, source package.
              const effectiveEnabled = hasDraft ? draft.enabled : (f.source === "override" ? f.enabled : (data.enabled_keys.includes(f.feature_key)));
              // Package baseline (tanpa override) — deduce dari feature.enabled saat source=package
              const packageDefault = f.source === "package" ? f.enabled : !f.enabled; // toggle backward-guess kalau override
              return (
                <div key={f.feature_key} className="px-4 py-3 border-t border-white/5 grid grid-cols-1 md:grid-cols-12 gap-2 items-center" data-testid={`override-row-${f.feature_key}`}>
                  <div className="md:col-span-5 min-w-0">
                    <div className="font-mono text-xs text-white/70">{f.feature_key}</div>
                    <div className="text-sm text-white/90 font-bold">{f.feature_name}</div>
                    <div className="text-[10px] text-white/40 mt-0.5">
                      Package default: <span className={packageDefault ? "text-emerald-400" : "text-rose-400"}>{packageDefault ? "ENABLED" : "DISABLED"}</span>
                    </div>
                  </div>
                  <div className="md:col-span-2">
                    <FeatureSourceBadge source={hasDraft ? "override" : f.source} />
                  </div>
                  <div className="md:col-span-2 flex gap-1.5">
                    <button type="button" onClick={() => setOverride(f.feature_key, true, draft?.reason)}
                      className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                        hasDraft && draft.enabled ? "bg-emerald-500 text-white" : "bg-white/5 text-white/60 hover:bg-white/10"
                      }`} data-testid={`btn-override-enable-${f.feature_key}`}>ENABLE</button>
                    <button type="button" onClick={() => setOverride(f.feature_key, false, draft?.reason)}
                      className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider ${
                        hasDraft && !draft.enabled ? "bg-rose-500 text-white" : "bg-white/5 text-white/60 hover:bg-white/10"
                      }`} data-testid={`btn-override-disable-${f.feature_key}`}>DISABLE</button>
                    {hasDraft && (
                      <button type="button" onClick={() => clearOverride(f.feature_key)}
                        className="px-2 py-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10"
                        title="Reset override (pakai package default)" data-testid={`btn-override-clear-${f.feature_key}`}>
                        <RotateCcw size={11} />
                      </button>
                    )}
                  </div>
                  <div className="md:col-span-3">
                    {hasDraft && (
                      <Input value={draft.reason || ""} onChange={(e) => setOverride(f.feature_key, draft.enabled, e.target.value)}
                        placeholder="Alasan override…" className="bg-white/5 border-white/10 text-white text-xs h-8"
                        data-testid={`override-reason-${f.feature_key}`} />
                    )}
                  </div>
                  <div className="hidden md:block md:col-span-12 text-[10px] text-white/30 -mt-1">
                    Effective: <span className={effectiveEnabled ? "text-emerald-400" : "text-rose-400"}>{effectiveEnabled ? "ENABLED" : "DISABLED"}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
